import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getAmplitudeInstance } from '@alltrails/shared/utils/amplitudeHelpers';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { UNITED_STATES_ID } from 'utils/constants/Country';
import { StringConstantsMixin, ProPageStrings } from 'react_components/mixins/shared/string_constants_mixin';
import SectionDivider from 'components/shared/SectionDivider';
import Button from 'components/shared/Button';
import { loadRecaptcha, executeRecaptcha } from 'utils/recaptcha_helpers';
import { validateUserForm } from 'utils/form_validaters/user_form_helper';
import { facebookHandler, appleHandler, googleHandler } from 'utils/oauth_handlers';
import { ServerCommunicationUtil } from 'utils/server_communication_util';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { OptimizelyConstants } from 'utils/optimizely_helpers';
import { EMAIL, FACEBOOK, GOOGLE } from 'utils/constants/LoginType';
import { SIGN_UP_SUCCEEDED } from 'utils/constants/AmplitudeEvent';
import OutlinedTextField from '../textFields/OutlinedTextField';
import Checkbox from '../Checkbox';
import AltLoginsForm from '../AltLoginsForm';
import * as baseStyles from '../../../styles/baseStyles.module.scss';
import * as formStyles from '../styles/styles.module.scss';
import * as styles from './styles/styles.module.scss';
import logError from '../../../utils/logError';

const {
  formText: { login, loginLead }
} = ProPageStrings;

const {
  OR,
  SIGNUP: { SIGNUP }
} = StringConstantsMixin.strings;

function trackRegistrationInTatari() {
  // Safety first - Wrap anything Tatari-related in a try/catch
  try {
    if (window.tatari) {
      window.tatari.track('signup');
    }
  } catch (error) {
    logError(error);
  }
}

const REGISTRATION_URL = '/api/alltrails/register';

const LOGIN_PAGE_URL = '/login';

function RegistrationForm({
  countryId,
  enableRecaptcha,
  facebookAppId,
  handleOptimizelyEvent,
  handleRegistrationSuccess,
  redirectTo,
  text,
  userEmailSubscribed,
  handleLoginClick,
  referralLink
}) {
  const [state, setState] = useState({
    userRegEmail: '',
    userRegPassword: '',
    userRegFirstName: '',
    userRegLastName: '',
    userEmailSubscribed,
    shownErrors: {},
    serverError: null,
    submitting: false,
    disableSubmit: enableRecaptcha,
    recaptchaObject: null,
    showErrorsOnType: false
  });

  const [errors, setErrors] = useState({});

  const locale = useLanguageRegionCode();

  useEffect(() => {
    if (enableRecaptcha) {
      loadRecaptcha(recaptchaObject => {
        setState({ ...state, disableSubmit: false, recaptchaObject });
      });
    }
  }, []);

  function showEmailSubscribed() {
    return countryId !== UNITED_STATES_ID;
  }

  function handleSubscribedChange(e) {
    e.preventDefault();
    if (!showEmailSubscribed()) {
      return;
    }

    const { userEmailSubscribed } = state;
    setState({ ...state, userEmailSubscribed: !userEmailSubscribed });
  }

  function registrationSuccessCallback(data, signupMethod) {
    if (handleOptimizelyEvent) {
      handleOptimizelyEvent(OptimizelyConstants.EVENTS.SIGNUP_SUCCESS, OptimizelyConstants.OUTCOMES.SUCCESS, { signupMethod });
    }
    if (handleRegistrationSuccess) {
      handleRegistrationSuccess(data);
    }

    trackRegistrationInTatari();

    setErrors({});

    setState({
      serverError: null
    });

    getAmplitudeInstance().logEvent(SIGN_UP_SUCCEEDED, {
      'login type': signupMethod
    });

    if (!redirectTo) {
      return;
    }

    window.location.href = redirectTo;
  }

  function handleGoogle(response) {
    googleHandler(
      response,
      locale,
      data => registrationSuccessCallback(data, GOOGLE),
      () => {},
      () => null
    );
  }

  function handleFacebook(response) {
    facebookHandler(
      response,
      locale,
      data => registrationSuccessCallback(data, FACEBOOK),
      () => {},
      () => null
    );
  }

  function handleChange(e) {
    e.preventDefault();
    const { name, value } = e.target;
    // we don't want to show errors on type unless a user has already tried to submit a form
    if (state.showErrorsOnType) {
      setErrors(validateUserForm(name, value, errors).errors);
    }

    setState({ ...state, [name]: value });
  }

  function handleErrorCallback(err) {
    let { serverError } = state;
    const serverResponseError = err && err.responseJSON ? err.responseJSON.errors[0] : { message: StringConstantsMixin.strings.ERR_TRY_AGAIN };
    if (serverResponseError && serverResponseError.target === 'email') {
      setErrors({ ...errors, userRegEmail: serverResponseError.message });
      serverError = serverResponseError.message;
    } else if (serverResponseError && serverResponseError.message) {
      serverError = serverResponseError.message;
    }
    setState({ ...state, serverError, submitting: false });
    if (handleOptimizelyEvent) {
      handleOptimizelyEvent(OptimizelyConstants.EVENTS.SIGNUP_CLICKED, OptimizelyConstants.OUTCOMES.FAILED_VALIDATION);
    }
  }

  function submitRegister(recaptchaToken) {
    // API params
    // required =>  :first_name, :last_name, :email, :password
    // optional => :create_session, :subscribed, :recaptcha
    const headers = locale ? { 'X-Language-Locale': locale } : {};
    const params = {
      first_name: state.userRegFirstName,
      last_name: state.userRegLastName,
      email: state.userRegEmail,
      password: state.userRegPassword,
      create_session: true,
      subscribed: state.userEmailSubscribed,
      recaptcha: recaptchaToken,
      referral_code: referralLink
    };

    ServerCommunicationUtil.postApiEndpoint(
      REGISTRATION_URL,
      params,
      data => registrationSuccessCallback(data, EMAIL),
      handleErrorCallback,
      () => null,
      headers
    );
  }

  function handleRegister(e) {
    e.preventDefault();
    if (state.disableSubmit || state.submitting) {
      return;
    }

    let validationErrors = {};
    validationErrors = validateUserForm('userRegFirstName', state.userRegFirstName, validationErrors).errors;
    validationErrors = validateUserForm('userRegLastName', state.userRegLastName, validationErrors).errors;
    validationErrors = validateUserForm('userRegEmail', state.userRegEmail, validationErrors).errors;
    validationErrors = validateUserForm('userRegPassword', state.userRegPassword, validationErrors).errors;

    if (Object.keys(validationErrors).length !== 0) {
      setErrors(validationErrors);
      setState({ ...state, showErrorsOnType: true });

      if (handleOptimizelyEvent) {
        handleOptimizelyEvent(OptimizelyConstants.EVENTS.SIGNUP_CLICKED, OptimizelyConstants.OUTCOMES.FAILED_VALIDATION);
      }

      return;
    }

    setState({ ...state, submitting: true });
    // Need to get recaptchaToken here/now, because a token can only be used once,
    // and server may reject form details requiring the user to edit and resubmit.
    if (enableRecaptcha) {
      executeRecaptcha(state.recaptchaObject, 'userSignupLogin').then(
        recaptchaToken => submitRegister(recaptchaToken),
        () => setState({ ...state, submitting: false })
      );
    } else {
      submitRegister();
    }
  }

  function renderEmailTextField() {
    return (
      <div className={`${formStyles.inputItem}`} data-testid="registrationForm-emailTextFeild">
        <OutlinedTextField
          name="userRegEmail"
          errorMessage={errors.userRegEmail}
          value={state.userRegEmail}
          label={text.email.title}
          changeHandler={e => handleChange(e)}
          enterKeyHandler={handleRegister}
          type="email"
        />
      </div>
    );
  }

  function renderPasswordTextField() {
    return (
      <div
        className={`${formStyles.inputItem} ${showEmailSubscribed() ? styles.showSubscribedDistance : ''} `}
        data-testid="registrationForm-passwordTextFeild"
      >
        <OutlinedTextField
          name="userRegPassword"
          errorMessage={errors.userRegPassword}
          value={state.userRegPassword}
          label={text.password.title}
          changeHandler={e => handleChange(e)}
          enterKeyHandler={handleRegister}
          autoComplete={false}
          type="password"
        />
      </div>
    );
  }

  function renderEmailOptinCheckbox() {
    if (!showEmailSubscribed()) {
      return null;
    }

    return (
      <Checkbox
        handleChange={handleSubscribedChange}
        checked={state.userEmailSubscribed}
        label={StringConstantsMixin.strings.SIGNUP.EMAIL_SUBSCRIBE}
      />
    );
  }

  function renderServerError() {
    if (!state.serverError) {
      return null;
    }

    return <div className={`${formStyles.serverError} ${styles.center}`}>{state.serverError}</div>;
  }

  function renderFormFields() {
    const disabled = state.disableSubmit || state.submitting;

    return (
      <form onSubmit={handleRegister}>
        <div className={`${formStyles.inputItem}`} data-testid="registrationForm-firstNameTextField">
          <OutlinedTextField
            name="userRegFirstName"
            errorMessage={errors.userRegFirstName}
            value={state.userRegFirstName}
            label={text.firstName.title}
            changeHandler={e => handleChange(e)}
            enterKeyHandler={handleRegister}
          />
        </div>
        <div className={`${formStyles.inputItem}`} data-testid="registrationForm-lastNameTextField">
          <OutlinedTextField
            name="userRegLastName"
            errorMessage={errors.userRegLastName}
            value={state.userRegLastName}
            label={text.lastName.title}
            changeHandler={e => handleChange(e)}
            enterKeyHandler={handleRegister}
          />
        </div>
        {renderEmailTextField()}
        {renderPasswordTextField()}
        {renderEmailOptinCheckbox()}
        {renderServerError()}
        <Button classes={styles.signup} disabled={disabled} type="submit" variant="primary">
          {SIGNUP}
        </Button>
      </form>
    );
  }

  return (
    <div>
      <AltLoginsForm
        facebookAppId={facebookAppId}
        googleLoginHandler={handleGoogle}
        facebookLoginHandler={handleFacebook}
        appleLoginHandler={appleHandler}
      />
      <SectionDivider classes={styles.sectionDivider}>{OR}</SectionDivider>
      <div className={baseStyles.section}>{renderFormFields()}</div>
      <div className={styles.alreadyHaveAnAccount}>
        {loginLead}{' '}
        <a className={baseStyles.route} href={LanguageSupportUtil.wrapUrlSafe(LOGIN_PAGE_URL, locale)} onClick={handleLoginClick}>
          {login}
        </a>
      </div>
    </div>
  );
}

const titleAndValidation = PropTypes.shape({
  title: PropTypes.string,
  validation: PropTypes.shape({
    generic: PropTypes.string
  })
});

RegistrationForm.defaultProps = {
  referralLink: null
};

RegistrationForm.propTypes = {
  enableRecaptcha: PropTypes.bool.isRequired,
  facebookAppId: PropTypes.number.isRequired,
  countryId: PropTypes.number.isRequired,
  text: PropTypes.shape({
    headText: PropTypes.string,
    stepText: PropTypes.string,
    forgotPassword: PropTypes.string,
    register: PropTypes.string,
    email: titleAndValidation,
    firstName: titleAndValidation,
    lastName: titleAndValidation,
    password: titleAndValidation,
    loginLead: PropTypes.string,
    login: PropTypes.string,
    privacyPolicyLead: PropTypes.string,
    termsLink: PropTypes.string,
    appleLogin: PropTypes.string,
    facebookLogin: PropTypes.string,
    googleLogin: PropTypes.string
  }).isRequired,
  referralLink: PropTypes.string
};

export default RegistrationForm;
